/**
 * Utility functions for player sorting
 */

/**
 * Parse and validate jersey number
 * @param {string|number} jerseyNumber - The jersey number to parse
 * @returns {number} Parsed number (1-99) or 999 for invalid/missing numbers
 */
export const parseJerseyNumber = (jerseyNumber) => {
  // Handle null, undefined, empty string
  if (!jerseyNumber && jerseyNumber !== 0) {
    return 999;
  }

  const num = parseInt(jerseyNumber, 10);
  
  // Check if valid number and within reasonable range for jersey numbers
  if (isNaN(num) || num < 1 || num > 99) {
    return 999;
  }
  
  return num;
};

/**
 * Sort players by jersey number first, then by name
 * Returns a new array without mutating the original
 * @param {Array} players - Array of player objects
 * @returns {Array} Sorted array of players
 */
export const sortPlayersByJerseyAndName = (players) => {
  // Create a shallow copy to avoid mutating the original array
  return [...players].sort((a, b) => {
    // Sort by jersey number first
    const numberA = parseJerseyNumber(a.jerseyNumber);
    const numberB = parseJerseyNumber(b.jerseyNumber);
    
    if (numberA !== numberB) {
      return numberA - numberB;
    }
    
    // Then sort by name (case-insensitive)
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Sort matches by date, newest first
 * Returns a new array without mutating the original
 * @param {Array} matches - Array of match objects
 * @returns {Array} Sorted array of matches
 */
export const sortMatchesByDate = (matches) => {
  return [...matches].sort((a, b) => {
    const dateA = new Date(a.date || 0);
    const dateB = new Date(b.date || 0);
    return dateB - dateA; // Newest first
  });
};